import { LocalStorageCache } from '@auth0/auth0-react';

const METADATA_PREFIX = 'agora-refresh-family:';
const DAY_MS = 86_400_000;

type Entry = { body?: { refresh_token?: unknown } };
const hasRefreshToken = (entry: unknown): boolean =>
  typeof (entry as Entry | undefined)?.body?.refresh_token === 'string' &&
  Boolean((entry as Entry).body?.refresh_token);

export const refreshCachePrefix = (clientId: string, audience: string): string =>
  `@@auth0spajs@@::${clientId}::${audience}::`;

/** Keeps the SDK's cache unchanged. Only non-secret issuance dates are added. */
export class DiagnosticAuthCache extends LocalStorageCache {
  private readonly prefix: string;
  lastWrittenRefreshKey: string | null = null;

  constructor(clientId: string, audience: string) {
    super();
    this.prefix = refreshCachePrefix(clientId, audience);
  }

  set<T>(key: string, entry: T): void {
    let previouslyHadRefreshToken = false;
    try {
      previouslyHadRefreshToken = key.startsWith(this.prefix) && hasRefreshToken(super.get(key));
    } catch {
      // A diagnostic read must not prevent a cache write that the SDK can make.
    }
    super.set(key, entry);
    if (key.startsWith(this.prefix) && hasRefreshToken(entry)) this.lastWrittenRefreshKey = key;
    // A missing/rejected RT followed by iframe authorization starts an untracked
    // family. Never keep the previous family's age for that new authorization.
    if (key.startsWith(this.prefix) && (!previouslyHadRefreshToken || !hasRefreshToken(entry))) {
      clearFamilyDate(key);
    }
  }

  remove(key: string): void {
    super.remove(key);
    if (key.startsWith(this.prefix)) clearFamilyDate(key);
  }
}

const clearFamilyDate = (key: string): void => {
  try {
    localStorage.removeItem(METADATA_PREFIX + key);
  } catch {
    // Diagnostics must never change the result of an SDK cache operation.
  }
};

export interface RefreshTokenSnapshot {
  has_refresh_token: boolean;
  refresh_token_family_age_days: number | null;
  refresh_token_age_known: boolean;
}

/** Called only after the SDK validates an authorization-code callback. */
export const recordRefreshFamilyIssuance = (
  clientId: string,
  audience: string,
  key: string | null
): boolean => {
  let found = false;
  try {
    const prefix = refreshCachePrefix(clientId, audience);
    if (!key?.startsWith(prefix) || !hasRefreshToken(JSON.parse(localStorage.getItem(key) || 'null')))
      return false;
    found = true;
    localStorage.setItem(METADATA_PREFIX + key, String(Date.now()));
  } catch {
    // The login may still succeed with unavailable diagnostic storage.
  }
  return found;
};

/** Returns booleans/durations only. No token, subject, cache entry or cache key escapes. */
export const snapshotRefreshToken = (clientId: string, audience: string): RefreshTokenSnapshot => {
  const ages: Array<number | null> = [];
  try {
    const prefix = refreshCachePrefix(clientId, audience);
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(prefix)) continue;
      let entry: unknown;
      try {
        entry = JSON.parse(localStorage.getItem(key) || 'null');
      } catch {
        continue;
      }
      if (!hasRefreshToken(entry)) continue;
      const issuedAt = Number(localStorage.getItem(METADATA_PREFIX + key));
      const age = Date.now() - issuedAt;
      ages.push(Number.isFinite(issuedAt) && issuedAt > 0 && age >= 0 ? Math.floor(age / DAY_MS) : null);
    }
  } catch {
    // storage_available in the enclosing boot snapshot distinguishes this case.
  }
  const known = ages.length > 0 && ages.every((age) => age !== null);
  return {
    has_refresh_token: ages.length > 0,
    refresh_token_family_age_days: known ? Math.max(...(ages as number[])) : null,
    refresh_token_age_known: known
  };
};
